import { GenderQueryKeys } from '../../queryKey';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchGenders } from './service';
import { useState } from 'react';
import {
  GenericError,
  PaginatedRequest,
  PaginatedResponse,
} from '@starsoft/common/models';
import { useAxiosInstance, usePaginatedQuery } from '@starsoft/common/hooks';
import { UseGendersQueryKeyProps } from './props';
import { Gender } from '@starsoft/common/models/gender';

/**
 * Custom hook to fetch and manage paginated gender data
 *
 * This hook handles fetching genders with pagination support, managing the query cache,
 * and providing pagination controls.
 *
 * @returns {Object} An object containing:
 *   - page: Current page number
 *   - data: Paginated gender data
 *   - isPending: Boolean indicating if query is in progress and not idle
 *   - fetchNextPage: Function to load next page
 *   - fetchPreviousPage: Function to load previous page
 *   - setCurrentPage: Function to set specific page number
 *   - Additional query properties spread from React Query
 */
export function useGenders() {
  const [page, setPage] = useState<number>(1);

  // Configure pagination parameters
  const params: PaginatedRequest = {
    page,
    limit: 20,
  };

  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Set up query key for caching
  const queryKey: UseGendersQueryKeyProps = [GenderQueryKeys.Primary, params];

  // Execute query to fetch gender data
  const { data, isPending, fetchStatus, ...query } = useQuery<
    PaginatedResponse<Gender>,
    AxiosError<GenericError>,
    PaginatedResponse<Gender>,
    UseGendersQueryKeyProps
  >({
    queryKey,
    queryFn: queryContext => fetchGenders(queryContext, axiosInstance),
    staleTime: 5 * 1000, // Consider data stale after 5 seconds
  });

  // Set up pagination callback handlers
  const callbacks = usePaginatedQuery({
    data,
    page,
    setPage,
  });

  return {
    ...query,
    ...callbacks,
    page,
    data,
    isPending: isPending && fetchStatus !== 'idle',
  };
}
